import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DocumentViewer from './DocumentViewer';
import SignatureCanvas from './SignatureCanvas';
import './SigningApp.css';

/**
 * Candidate-facing mobile document signing page.
 * Route: /sign/:code
 * No authentication required — gated by the event sign_code.
 * Candidates enter their name, then review and sign required documents.
 */
export default function SigningApp() {
  const { code } = useParams();
  const [status, setStatus] = useState('loading'); // loading | name_entry | ready | submitting | done | error
  const [payload, setPayload] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Name entry
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState('');

  // Document signing
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [agreed, setAgreed] = useState({});
  const [submitError, setSubmitError] = useState('');

  const sigRefs = useRef({});

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`/api/sign/${code}`);
        setPayload(res.data);
        setStatus('name_entry');
      } catch (e) {
        const msg = e.response?.data?.error || 'This signing link is invalid or has expired.';
        setErrorMsg(msg);
        setStatus('error');
      }
    }
    load();
  }, [code]);

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setNameError('Please enter your first and last name.');
      return;
    }
    setNameError('');
    setStatus('ready');
  };

  const requiredDocs = payload?.documents?.filter((d) => d.is_required) || [];
  const currentDoc = requiredDocs[currentDocIndex];
  const isLastDoc = currentDocIndex === requiredDocs.length - 1;

  const handleNext = () => {
    if (!agreed[currentDoc.id]) {
      setSubmitError('Please check the box to confirm you have read this document.');
      return;
    }
    setSubmitError('');
    setCurrentDocIndex((i) => i + 1);
  };

  const handleSubmit = async () => {
    if (!agreed[currentDoc.id]) {
      setSubmitError('Please check the box to confirm you have read this document.');
      return;
    }
    setSubmitError('');

    const signatures = requiredDocs.map((doc) => {
      const sigRef = sigRefs.current[doc.id];
      const sigData = sigRef?.getDataUrl() || null;
      return { document_id: doc.id, signature_png: sigData };
    }).filter((s) => s.signature_png);

    if (signatures.length === 0) {
      setSubmitError('Please sign at least one document.');
      return;
    }

    setStatus('submitting');
    try {
      await axios.post(`/api/sign/${code}/submit`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        signatures
      });
      setStatus('done');
    } catch (e) {
      const msg = e.response?.data?.error || 'Submission failed. Please try again.';
      setSubmitError(msg);
      setStatus('ready');
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="signing-app">
        <div className="signing-loading">
          <div className="spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="signing-app">
        <div className="signing-error-screen">
          <h2>Link Not Found</h2>
          <p>{errorMsg}</p>
          <p className="signing-error-hint">Ask the registration staff for the correct signing link.</p>
        </div>
      </div>
    );
  }

  // ── Name entry ───────────────────────────────────────────────────────
  if (status === 'name_entry') {
    return (
      <div className="signing-app">
        <header className="signing-header">
          <div className="signing-event-name">{payload.event.name}</div>
          <div className="signing-candidate-name">Candidate Check-In</div>
        </header>
        <div className="signing-content">
          <div className="signing-name-entry">
            <h2>Enter Your Name</h2>
            <p className="signing-name-instructions">
              Please enter your name exactly as it appears on your ID.
            </p>
            <form onSubmit={handleNameSubmit} className="name-entry-form">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input
                  className="form-input"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoFocus
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  className="form-input"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
              {nameError && <p className="signing-submit-error">{nameError}</p>}
              {requiredDocs.length === 0 ? (
                <div className="signing-no-docs">
                  <div className="signing-done-icon">✓</div>
                  <p>No documents are required for this event.</p>
                  <p className="signing-done-hint">You may close this page and return to the registration desk.</p>
                </div>
              ) : (
                <button type="submit" className="btn btn-primary btn-block">
                  Continue to Documents →
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Done ────────────────────────────────────────────────────────────
  if (status === 'done') {
    return (
      <div className="signing-app">
        <div className="signing-done-screen">
          <div className="signing-done-icon">✓</div>
          <h2>Thank you, {firstName}!</h2>
          <p>Your documents have been signed for <strong>{payload.event.name}</strong>.</p>
          <p className="signing-done-hint">You may close this page and return to the registration desk.</p>
        </div>
      </div>
    );
  }

  // ── Document signing flow ───────────────────────────────────────────
  return (
    <div className="signing-app">
      <header className="signing-header">
        <div className="signing-event-name">{payload.event.name}</div>
        <div className="signing-candidate-name">
          {firstName} {lastName}
        </div>
      </header>

      <div className="signing-progress">
        {requiredDocs.map((doc, i) => (
          <div
            key={doc.id}
            className={`progress-step ${i < currentDocIndex ? 'done' : i === currentDocIndex ? 'active' : 'pending'}`}
          />
        ))}
      </div>

      <div className="signing-content">
        {currentDoc && (
          <div className="signing-document">
            <h2 className="document-title">{currentDoc.title}</h2>

            <div className="document-body">
              <DocumentViewer document={currentDoc} code={code} />
            </div>

            <div className="signature-section">
              <label className="agree-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!agreed[currentDoc.id]}
                  onChange={(e) =>
                    setAgreed((prev) => ({ ...prev, [currentDoc.id]: e.target.checked }))
                  }
                />
                <span>I have read and understand this document.</span>
              </label>

              {agreed[currentDoc.id] && (
                <SignatureCanvas
                  ref={(el) => { sigRefs.current[currentDoc.id] = el; }}
                  label="Sign your name below"
                />
              )}
            </div>

            {submitError && <p className="signing-submit-error">{submitError}</p>}

            <div className="signing-actions">
              {isLastDoc ? (
                <button
                  className="btn btn-primary btn-block"
                  onClick={handleSubmit}
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Submitting…' : 'Submit All Signatures'}
                </button>
              ) : (
                <button className="btn btn-primary btn-block" onClick={handleNext}>
                  Next Document →
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
